import axios from 'axios'
import type {
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationWithDepartments,
  Department,
  DepartmentCreate,
  DepartmentUpdate,
} from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE_URL,
})

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Organization API
export const organizationService = {
  // Get all organizations
  async getAll(includeInactive = false): Promise<Organization[]> {
    const response = await apiClient.get('/api/v1/organizations', {
      params: { include_inactive: includeInactive },
    })
    return response.data
  },

  // Get single organization with departments
  async getById(id: string): Promise<OrganizationWithDepartments> {
    const response = await apiClient.get(`/api/v1/organizations/${id}`)
    return response.data
  },

  // Create organization
  async create(data: OrganizationCreate): Promise<Organization> {
    const response = await apiClient.post('/api/v1/organizations', data)
    return response.data
  },

  // Update organization
  async update(id: string, data: OrganizationUpdate): Promise<Organization> {
    const response = await apiClient.patch(`/api/v1/organizations/${id}`, data)
    return response.data
  },

  // Delete organization
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/organizations/${id}`)
  },
}

// Department API
export const departmentService = {
  // Get all departments
  async getAll(organizationId?: string, includeInactive = false): Promise<Department[]> {
    const response = await apiClient.get('/api/v1/departments', {
      params: {
        organization_id: organizationId,
        include_inactive: includeInactive,
      },
    })
    return response.data
  },

  // Get single department
  async getById(id: string): Promise<Department> {
    const response = await apiClient.get(`/api/v1/departments/${id}`)
    return response.data
  },

  // Create department
  async create(data: DepartmentCreate): Promise<Department> {
    const response = await apiClient.post('/api/v1/departments', data)
    return response.data
  },

  // Update department
  async update(id: string, data: DepartmentUpdate): Promise<Department> {
    const response = await apiClient.patch(`/api/v1/departments/${id}`, data)
    return response.data
  },

  // Delete department
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/departments/${id}`)
  },
}
